import { Theme, css } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';
import React, { useEffect, useMemo, useState } from 'react';

export type CollapseProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  headerElement: React.ReactElement<React.HTMLAttributes<HTMLButtonElement>>;
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
};

const collapseStyles = ({ colors, borderRadius }: Theme) => css`
  & > button {
    width: 100%;
    border-radius: ${borderRadius[100]};
  }

  & > button:focus-visible {
    outline-offset: 2px;
    outline: 2px solid ${colors.borderPrimaryBase};
  }
`;

/**
 * @deprecated Deprecated — use pantry Accordion instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-accordion--docs
 * Migration: Replace Collapse with the pantry Accordion component.
 */
export const Collapse = ({
  headerElement,
  children,
  expanded = false,
  onChange,
  ...rest
}: CollapseProps) => {
  const [panelExpanded, setPanelExpanded] = useState(expanded);

  useEffect(() => {
    onChange?.(panelExpanded);
  }, [onChange, panelExpanded]);

  const headerId = useMemo(() => uuidv4(), []);
  const panelId = useMemo(() => uuidv4(), []);

  const handleHeaderButtonClick = () => {
    setPanelExpanded(!panelExpanded);
  };

  const headerElementWithProps = (expanded: boolean) =>
    React.cloneElement(headerElement, {
      'aria-expanded': expanded,
      'aria-controls': panelId,
      onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        handleHeaderButtonClick();
        headerElement.props.onClick?.(e);
      },
      id: headerId,
    });

  return (
    <div {...rest} css={collapseStyles}>
      {headerElementWithProps(panelExpanded)}
      <div
        id={panelId}
        aria-labelledby={headerId}
        role="region"
        css={{ display: panelExpanded ? 'block' : 'none' }}
      >
        {children}
      </div>
    </div>
  );
};
